/**
 * staleBuild.js — evict a client that is running a build the server no longer serves, and
 * tell a patient whose open tab has fallen behind a deploy.
 *
 * ★★★ WHY THIS EXISTS, AND IT IS NOT AN OPTIMISATION. On 2026-09-09 a bad build reached
 * production for roughly twenty minutes. The fix shipped, `/VERSION` answered the new number
 * from the very first check, the old bundle 404ed on the server — and the one person testing
 * it kept crashing, on the SAME minified names, because his browser was still running the
 * broken build out of cache. **A SERVICE WORKER THAT CAN PIN A PATIENT TO A BAD BUILD IS A
 * WORSE PROBLEM THAN THE BAD BUILD**, because the bad build is over in minutes and the pin is
 * not, and nothing in the app was in a position to notice.
 *
 * ★★ FOUR PIECES, SO EACH CAN BE MEASURED ALONE (scripts/check-stale-build.mjs):
 *   readServedVersion     reads `/VERSION` with `cache: 'no-store'` and compares it to
 *                         `APP_VERSION`, which `gen-version.cjs` writes into the bundle from
 *                         `package.json`. It only reads: it never evicts and never reloads.
 *   deleteCachesAndReload deletes every cache and reloads. It never touches the service
 *                         worker, and offline (/VERSION unreachable) it does nothing at all.
 *                         The notice's path.
 *   evictCachesAndReload  deletes every cache and reloads, with no second read of /VERSION.
 *                         It never touches the service worker either (P8 D4 addendum).
 *   evictIfStale          the at-load check (main.jsx, after `load`): readServedVersion and
 *                         evictCachesAndReload, composed, with the once-per-session guard
 *                         between them.
 *
 * ★★★ NOTHING HERE UNREGISTERS THE SERVICE WORKER (P8 review R1 = (b), 2026-09-26, for
 * the notice; P8 D4 addendum, 2026-09-27, for the at-load path). Unregistering a
 * registration deactivates its Web Push subscription (W3C Push API), and nothing in this
 * app subscribes again on its own: only a tap in Notification preferences does. Both paths
 * run after every deploy, so either would end the patient's push each time. public/sw.js
 * is network-first for navigations and assets, so deleting the caches and reloading
 * already loads the new bundle, and since P8 N1 every successful navigation writes the
 * offline copy of the app page again, so the worker needs no reinstall for it.
 *
 * ★★★ THE ONCE IS THE WHOLE SAFETY ARGUMENT. A reload loop on a patient's phone is far worse
 * than a stale bundle, so a sessionStorage key records the attempt BEFORE the reload; if the
 * versions still disagree afterwards, the difference is not something reloading can fix
 * (a proxy, an offline device, a half-deployed CDN) and this gives up and lets the app run.
 * It never blocks rendering and it never reloads twice.
 * ★★ AND THE GUARD MUST BE ABLE TO HOLD THE ATTEMPT (D5, 2026-09-26). When a browser blocks
 * sessionStorage the write was swallowed and the reload happened anyway, so every load
 * evicted and reloaded again: a loop whenever the reloaded bundle still differed from
 * `/VERSION`. The attempt is now written and READ BACK; unless it reads back equal, this
 * returns 'guard-unavailable' and does not reload at all. At most one automatic reload per
 * page load, never a loop.
 *
 * ★ THE "NEW VERSION" NOTICE (Alex's decision of 2026-09-24, TEST 2.18). A tab left open
 * across a deploy keeps running the old build until something notices. components/
 * UpdateNotice.jsx checks with readServedVersion when the tab becomes visible and every five
 * minutes while it is, and offers a reload; only the patient's tap calls
 * deleteCachesAndReload. It also shows when the at-load check gave up or could not guard.
 * The silent once-per-load reload at startup stays under it as a safety net (D4).
 */
import { APP_VERSION } from './appVersion.js'

export const ATTEMPT_KEY = 'orella_stale_build_reload'
// Dispatched on window by main.jsx when the at-load check ends in 'gave-up' or
// 'guard-unavailable', so the notice can offer the reload this module did not make.
export const STALE_LOAD_EVENT = 'orella:stale-build-at-load'

const VERSION_RE = /^v\d+\.\d+\.\d+$/

const defaultStorage = () => {
  try { return window.sessionStorage } catch { return null }
}
const readAttempt = (storage) => {
  try { return storage ? storage.getItem(ATTEMPT_KEY) : null } catch { return null }
}
// True only when the attempt demonstrably persisted: written, then read back equal.
const writeAttempt = (storage, v) => {
  try {
    if (!storage) return false
    storage.setItem(ATTEMPT_KEY, v)
    return storage.getItem(ATTEMPT_KEY) === v
  } catch {
    return false
  }
}

/**
 * Read `/VERSION` and compare it to the running build. Never evicts, never reloads.
 * Returns { state: 'current' | 'stale' | 'unreachable' | 'unreadable', served }.
 */
export async function readServedVersion({ fetchImpl = fetch, running = APP_VERSION } = {}) {
  let served
  try {
    const res = await fetchImpl('/VERSION', { cache: 'no-store' })
    if (!res || !res.ok) return { state: 'unreachable', served: null }
    served = (await res.text()).trim()
  } catch {
    return { state: 'unreachable', served: null }          // offline is not stale
  }
  if (!served || !VERSION_RE.test(served)) {
    console.warn(`[staleBuild] /VERSION did not answer a version; it answered ${JSON.stringify(String(served).slice(0, 80))}`)
    return { state: 'unreadable', served: null }
  }
  return { state: served === running ? 'current' : 'stale', served }
}

/**
 * The "Update now" tap. Deletes every cache and reloads; never touches the service worker
 * (R1 = (b)). Returns 'offline' (nothing deleted, no reload) when /VERSION is unreachable,
 * else 'reloaded'. ★★ P8 review N1: offline, deleting the caches removed the only copy of
 * the app page, and the reload then landed on public/sw.js's meta-refresh fallback, which
 * refreshes every 0 s while offline. Offline there is nothing a reload could fetch anyway.
 */
export async function deleteCachesAndReload({
  fetchImpl = fetch,
  reload = () => window.location.reload(),
  cachesImpl = typeof caches !== 'undefined' ? caches : undefined,
} = {}) {
  const { state } = await readServedVersion({ fetchImpl })
  if (state === 'unreachable') return 'offline'
  await dropCachesAndReload({ reload, cachesImpl })
  return 'reloaded'
}

// Delete every cache, then reload, with no check. The at-load path's second half (D4): it
// runs only after evictIfStale read a stale /VERSION, so it reads nothing again.
async function dropCachesAndReload({ reload, cachesImpl }) {
  try {
    if (cachesImpl) {
      const keys = await cachesImpl.keys()
      await Promise.all(keys.map((k) => cachesImpl.delete(k)))
    }
  } catch { /* nothing else to try */ }
  reload()
}

/**
 * Delete every cache and reload. At-load path only. ★★ P8 D4 addendum (2026-09-27): it
 * never unregisters the service worker, so the patient's Web Push subscription survives.
 * The next online navigation goes through the kept worker, which writes the offline copy
 * of the app page again (public/sw.js, P8 N1). check-stale-build SB-12 holds both.
 */
export async function evictCachesAndReload({
  reload = () => window.location.reload(),
  cachesImpl = typeof caches !== 'undefined' ? caches : undefined,
} = {}) {
  await dropCachesAndReload({ reload, cachesImpl })
}

/**
 * The at-load check. Returns 'current' | 'unreachable' | 'unreadable' | 'gave-up' |
 * 'guard-unavailable' | 'evicted'. Reloads only on 'evicted', and at most once per session.
 */
export async function evictIfStale({
  fetchImpl = fetch,
  reload = () => window.location.reload(),
  storage = defaultStorage(),
  cachesImpl,
} = {}) {
  const { state, served } = await readServedVersion({ fetchImpl })
  if (state !== 'stale') return state

  // ★ The attempt is recorded BEFORE the reload, never after — code after a reload does not run.
  if (readAttempt(storage) === served) {
    console.warn(`[staleBuild] running ${APP_VERSION}, server serves ${served}, and a reload did not change that. Giving up rather than looping.`)
    return 'gave-up'
  }
  if (!writeAttempt(storage, served)) {
    console.warn(`[staleBuild] running ${APP_VERSION}, server serves ${served}, and sessionStorage cannot hold the once-guard. Not reloading, so this cannot loop.`)
    return 'guard-unavailable'
  }
  console.warn(`[staleBuild] running ${APP_VERSION}, server serves ${served} — evicting caches and reloading once.`)
  const args = { reload }
  if (cachesImpl !== undefined) args.cachesImpl = cachesImpl
  await evictCachesAndReload(args)
  return 'evicted'
}
